/* ==============================================================================
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
============================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import TableHeader from 'components/Table/TableHeader';

const initHeader = [
  { id: 'cs.code', label: 'รอบ', align: 'center' },
  { id: 'br.name', label: 'สาขา', align: 'left' },
  { id: 'c.name', label: 'POS', align: 'left' },
  { id: 'cashier_name', label: 'ผู้ขาย', align: 'left' },
  { id: 'tb.created_at', label: 'วันที่', align: 'center' },
  { id: '', label: '', align: 'center' },
  { id: '', label: '', align: 'center' },
];

function PosCloseShift() {
  const mod = 'closeshift_request';
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);
  const [record, setRecord] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const txtSearch = useRef(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = () => {
    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: txtSearch.current.value,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
      } else {
        Func.exitApp();
      }
    });
  };

  const edit = (item, status) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/edit`, app.token, {
          id: item.id,
          status: status,
        })
          .then((result) => {
            if (result.status === 'ok') {
              view();
              showNotify(t(`alert.editcomplete`), 'success');
            } else {
              showNotify(result.msg, 'error');
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  const btnApprove = (item) => {
    return (
      <MDButton
        variant='text'
        color='success'
        size='small'
        sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        onClick={() => edit(item, 'counting')}
      >
        อนุมัติ
      </MDButton>
    );
  };

  const btnCancel = (item) => {
    return (
      <MDButton
        variant='text'
        color='error'
        size='small'
        sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        onClick={() => edit(item, 'reject')}
      >
        ปฏิเสธ
      </MDButton>
    );
  };

  const renderSearch = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();
          view();
        }}
      >
        <MDInput
          name='searchform'
          fullWidth
          type='search'
          InputProps={{
            inputRef: txtSearch,
            startAdornment: (
              <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
            ),
            autoComplete: 'off',
          }}
        />
      </MDBox>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderOrder = () => {
    return (
      <MDBox sx={{ minHeight: 500 }}>
        <MDBox mb={2}>{renderSearch()}</MDBox>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHeader data={initHeader} />
            <TableBody>
              {record.map((row, i) => (
                <TableRow className='row-order' key={row.id}>
                  <TableCell align='center' className='nowrap'>
                    {row.code}
                  </TableCell>
                  <TableCell align='left' className='nowrap'>
                    {row.branch_name}
                  </TableCell>
                  <TableCell align='left' className='nowrap'>
                    {row.pos_code}
                  </TableCell>
                  <TableCell align='left'>{row.cashier_name}</TableCell>
                  <TableCell align='center' className='nowrap'>
                    {Func.dateTimeDisplay(row.created_at)}
                  </TableCell>
                  <TableCell align='center'>{btnApprove(row)}</TableCell>
                  <TableCell align='center'>{btnCancel(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view();
  }, []);

  return (
    <MDBox>
      {renderOrder()}
      {renderLoading()}
      {notify}
    </MDBox>
  );
}

export default PosCloseShift;
